import { useState } from "react";
import { CalendarDays } from "lucide-react";
import { format } from "date-fns";
import { nb } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export const RANGE_OPTIONS = [
  { key: "24h", label: "24 t" },
  { key: "7d", label: "7 d" },
  { key: "30d", label: "30 d" },
  { key: "90d", label: "90 d" },
  { key: "365d", label: "1 år" },
];

const segment = "h-7 rounded-[5px] px-2.5 text-xs font-medium text-muted-foreground hover:bg-transparent hover:text-foreground";
const segmentActive = "bg-card text-foreground shadow-[0_1px_2px_hsl(var(--brand-ink)/0.08)] hover:bg-card hover:text-foreground";

/**
 * Segmented time-range picker with optional custom interval (Calendar in Popover).
 * value: { range: '7d' | 'custom', start?: Date, end?: Date }
 */
export const TimeRangePicker = ({ value, onChange, options = RANGE_OPTIONS, allowCustom = true, className, testIdPrefix = "range" }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ from: value?.start, to: value?.end });
  const isCustom = value?.range === "custom";

  const apply = () => {
    if (draft?.from) {
      const to = draft.to || draft.from;
      onChange({ range: "custom", start: draft.from, end: new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59) });
      setOpen(false);
    }
  };

  return (
    <div className={cn("inline-flex max-w-full flex-wrap items-center gap-0.5 rounded-md bg-muted p-0.5", className)} role="group" aria-label="Velg periode">
      {options.map((o) => (
        <Button key={o.key} type="button" size="sm" variant="ghost" className={cn(segment, value?.range === o.key && segmentActive)} onClick={() => onChange({ range: o.key })} data-testid={`${testIdPrefix}-${o.key}`} aria-pressed={value?.range === o.key}>
          {o.label}
        </Button>
      ))}
      {allowCustom ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button type="button" size="sm" variant="ghost" className={cn(segment, "gap-1.5", isCustom && segmentActive)} data-testid={`${testIdPrefix}-custom`} aria-pressed={isCustom}>
              <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
              <span className={cn(!isCustom && "hidden sm:inline")}>{isCustom && value.start ? `${format(value.start, "d. MMM", { locale: nb })} – ${format(value.end, "d. MMM", { locale: nb })}` : "Egendefinert"}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-auto p-0">
            <Calendar mode="range" selected={draft} onSelect={setDraft} locale={nb} numberOfMonths={1} disabled={{ after: new Date() }} initialFocus />
            <div className="flex items-center justify-end gap-2 border-t p-2">
              <Button size="sm" variant="ghost" onClick={() => setOpen(false)} data-testid={`${testIdPrefix}-custom-cancel`}>
                Avbryt
              </Button>
              <Button size="sm" onClick={apply} disabled={!draft?.from} data-testid={`${testIdPrefix}-custom-apply`}>
                Bruk periode
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  );
};

export const rangeToParams = (value) => {
  if (!value) return { range: "7d" };
  if (value.range === "custom" && value.start) return { start: value.start.toISOString(), end: value.end.toISOString() };
  return { range: value.range };
};
